/**
 * Space-Entity Store
 *
 * Per-space LRU cache of entities, populated via the same two-stage fetch
 * the agent runtime uses (entityService.getEntitiesForSpace). Replaces the
 * dataspace-wide bulk-load of useEntityStore for space-scoped reads.
 *
 *   - Memory cache: up to MAX_CACHED_SPACES spaces (LRU).
 *   - Source-of-truth for sidebar / Cmd+K / app stores once flag is on.
 *   - Cleared on org switch.
 *
 * IDB persistence is added in Phase 2 (gui/src/services/idb/spaceEntityIDB.ts).
 *
 * See plans/entity_loading_refactor_8ae93a18.plan.md.
 */

import { create } from 'zustand';
import { entityService } from '../services/entityService';
import { getOrgEdgesSnapshot, subscribeToEntitySystemChanges } from './orgYDocStore';
import { useDataspaceTreeStore } from './dataspaceTreeStore';
import { useDataspaceContextStore } from './dataspaceContextStore';
import { readSnapshot, writeSnapshot, patchEntity as idbPatchEntity, deleteSnapshot } from '../services/idb/spaceEntityIDB';
import { createFileLogger } from '../utils/logger';
import type { BaseEntityType } from '../types/entities';

const log = createFileLogger('SPACE_ENTITY_STORE');

const MAX_CACHED_SPACES = 8;
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export interface SpaceCacheEntry {
  spaceId: string;
  entities: BaseEntityType[];
  entityById: Map<string, BaseEntityType>;
  fetchedAt: number;
  source: 'idb' | 'rest';
}

interface SpaceEntityState {
  caches: Map<string, SpaceCacheEntry>;
  loading: Set<string>;
  inflight: Map<string, Promise<void>>;
  bumpVersion: number;
}

interface SpaceEntityActions {
  /** Load entities for `spaceId`. Idempotent across concurrent callers. */
  initializeSpace: (spaceId: string, opts?: { force?: boolean }) => Promise<void>;
  /** Synchronous read of the cached entities for a space. Returns [] if not loaded. */
  getEntities: (spaceId: string) => BaseEntityType[];
  /** Synchronous read of a single entity from the space cache. */
  getEntity: (spaceId: string, entityId: string) => BaseEntityType | undefined;
  /** Patch an entity in the in-memory cache (e.g. when a Y.Doc node changes). */
  patchEntity: (spaceId: string, entityId: string, patch: Partial<BaseEntityType>) => void;
  /** Drop a space from memory. */
  evictSpace: (spaceId: string) => void;
  /** Drop everything (called on org switch). */
  clearAll: () => void;
}

export type SpaceEntityStore = SpaceEntityState & SpaceEntityActions;

function readEdgesSnapshot(): Array<{ parentId: string | null; childId: string }> {
  const orgEdges = getOrgEdgesSnapshot();
  if (orgEdges.length > 0) {
    return orgEdges.map((e) => ({ parentId: e.parentId, childId: e.childId }));
  }
  const treeNodes = useDataspaceTreeStore.getState().nodesById;
  const result: Array<{ parentId: string | null; childId: string }> = [];
  for (const [, node] of Object.entries(treeNodes)) {
    if (!node || !node.id) continue;
    result.push({ parentId: (node as any).parentId ?? null, childId: node.id });
  }
  return result;
}

function buildEntry(spaceId: string, entities: BaseEntityType[], source: SpaceCacheEntry['source']): SpaceCacheEntry {
  const entityById = new Map<string, BaseEntityType>();
  for (const e of entities) {
    if (e._id) entityById.set(e._id, e);
    if (e.id && e.id !== e._id) entityById.set(e.id, e);
  }
  return {
    spaceId,
    entities,
    entityById,
    fetchedAt: Date.now(),
    source,
  };
}

function lruInsert(
  caches: Map<string, SpaceCacheEntry>,
  entry: SpaceCacheEntry,
): Map<string, SpaceCacheEntry> {
  const next = new Map(caches);
  next.delete(entry.spaceId);
  next.set(entry.spaceId, entry);
  while (next.size > MAX_CACHED_SPACES) {
    const oldestKey = next.keys().next().value as string | undefined;
    if (!oldestKey) break;
    next.delete(oldestKey);
  }
  return next;
}

export const useSpaceEntityStore = create<SpaceEntityStore>((set, get) => ({
  caches: new Map(),
  loading: new Set(),
  inflight: new Map(),
  bumpVersion: 0,

  initializeSpace: async (spaceId: string, opts: { force?: boolean } = {}) => {
    if (!spaceId) return;

    const existingInflight = get().inflight.get(spaceId);
    if (existingInflight && !opts.force) {
      return existingInflight;
    }

    const currentDataspaceId = useDataspaceContextStore.getState().currentContext.orgId ?? null;

    const promise = (async () => {
      const t0 = performance.now();
      try {
        set((s) => {
          const loading = new Set(s.loading);
          loading.add(spaceId);
          return { loading };
        });

        // Stage 0 — paint from IDB if a fresh-enough snapshot exists for the
        // current dataspace. The REST refresh below is fire-and-forget unless
        // the cache is missing/stale, in which case we await it.
        const snapshot = await readSnapshot(spaceId);
        const cacheUsable =
          !!snapshot &&
          snapshot.meta.dataspaceId === currentDataspaceId &&
          Date.now() - snapshot.meta.fetchedAt <= STALE_AFTER_MS;

        if (cacheUsable && snapshot) {
          const idbEntry = buildEntry(spaceId, snapshot.entities, 'idb');
          set((s) => ({
            caches: lruInsert(s.caches, idbEntry),
            bumpVersion: s.bumpVersion + 1,
          }));
          log.info(
            `[SpaceEntityStore] 🪟 initializeSpace=${spaceId} idbHit count=${snapshot.entities.length} fetchedAt=${snapshot.meta.fetchedAt}`,
          );
        }

        const refreshPromise = (async () => {
          const edges = readEdgesSnapshot();
          const entities = await entityService.getEntitiesForSpace(spaceId, edges);
          const entry = buildEntry(spaceId, entities, 'rest');
          set((s) => ({
            caches: lruInsert(s.caches, entry),
            bumpVersion: s.bumpVersion + 1,
          }));
          await writeSnapshot(spaceId, entities, currentDataspaceId);
          log.info(
            `[SpaceEntityStore] ✅ initializeSpace=${spaceId} count=${entities.length} ms=${(performance.now() - t0).toFixed(0)} source=rest`,
          );
        })();

        if (!cacheUsable) {
          await refreshPromise;
        } else {
          // Background refresh — never throw out of stale-while-revalidate.
          refreshPromise.catch((err) =>
            log.warn(`[SpaceEntityStore] background refresh for ${spaceId} failed`, err),
          );
        }
      } catch (err) {
        log.error(`[SpaceEntityStore] ❌ initializeSpace=${spaceId} failed`, err);
        throw err;
      } finally {
        set((s) => {
          const loading = new Set(s.loading);
          loading.delete(spaceId);
          const inflight = new Map(s.inflight);
          inflight.delete(spaceId);
          return { loading, inflight };
        });
      }
    })();

    set((s) => {
      const inflight = new Map(s.inflight);
      inflight.set(spaceId, promise);
      return { inflight };
    });

    return promise;
  },

  getEntities: (spaceId: string) => {
    const entry = get().caches.get(spaceId);
    return entry?.entities ?? [];
  },

  getEntity: (spaceId: string, entityId: string) => {
    return get().caches.get(spaceId)?.entityById.get(entityId);
  },

  patchEntity: (spaceId: string, entityId: string, patch: Partial<BaseEntityType>) => {
    const entry = get().caches.get(spaceId);
    if (!entry) return;
    const existing = entry.entityById.get(entityId);
    if (!existing) return;
    const updated: BaseEntityType = { ...existing, ...patch };
    const nextById = new Map(entry.entityById);
    nextById.set(entityId, updated);
    if (updated.id && updated.id !== entityId) nextById.set(updated.id, updated);
    const nextList = entry.entities.map((e) =>
      e._id === entityId || e.id === entityId ? updated : e,
    );
    const nextEntry: SpaceCacheEntry = {
      ...entry,
      entities: nextList,
      entityById: nextById,
    };
    set((s) => ({
      caches: lruInsert(s.caches, nextEntry),
      bumpVersion: s.bumpVersion + 1,
    }));
  },

  evictSpace: (spaceId: string) => {
    set((s) => {
      const next = new Map(s.caches);
      next.delete(spaceId);
      return { caches: next, bumpVersion: s.bumpVersion + 1 };
    });
  },

  clearAll: () => {
    set({ caches: new Map(), bumpVersion: 0, loading: new Set(), inflight: new Map() });
  },
}));

// Stable reference for the empty-space fallback.
// Zustand's useSyncExternalStore compares snapshots with Object.is, so
// returning a new [] literal on every call when spaceId is null/undefined
// would make every render look "changed", causing an infinite re-render loop.
const EMPTY_ENTITIES: BaseEntityType[] = [];

/**
 * React hook — returns the cached entities for a space.
 * Callers should ensure initializeSpace has been kicked off (e.g. in
 * SpacePageWrapper). This hook intentionally does NOT auto-fetch to keep
 * render trees deterministic and avoid duplicate inflight requests.
 */
export function useSpaceEntities(spaceId: string | null | undefined): BaseEntityType[] {
  return useSpaceEntityStore((s) =>
    spaceId ? (s.caches.get(spaceId)?.entities ?? EMPTY_ENTITIES) : EMPTY_ENTITIES,
  );
}

export function useSpaceEntity(
  spaceId: string | null | undefined,
  entityId: string | null | undefined,
): BaseEntityType | undefined {
  return useSpaceEntityStore((s) =>
    spaceId && entityId ? s.caches.get(spaceId)?.entityById.get(entityId) : undefined,
  );
}

let _liveUnsubscribe: (() => void) | null = null;

/**
 * Subscribe to org Y.Doc node changes and pipe them into the per-space caches
 * and their IDB snapshots.
 *
 * Re-arm semantics: each call replaces the prior subscription. The org Y.Doc
 * is destroyed and recreated on every team switch (destroyOrgYDocStore clears
 * its `_entityListeners` set), so this function MUST be re-called after every
 * `initOrgYDocStore` or live updates would silently die after the first switch.
 *
 * `initEntitySystemYjsStore` (the canonical entry point that wraps
 * `initOrgYDocStore`) calls this for us, so most callers only need to invoke
 * it once at app startup.
 */
export function installSpaceEntityLiveUpdates(): void {
  if (_liveUnsubscribe) {
    _liveUnsubscribe();
    _liveUnsubscribe = null;
  }

  _liveUnsubscribe = subscribeToEntitySystemChanges((action, entityId, data) => {
    if (!entityId) return;
    const state = useSpaceEntityStore.getState();

    if (action === 'delete') {
      let mutated = false;
      const nextCaches = new Map(state.caches);
      for (const [spaceId, entry] of nextCaches) {
        if (!entry.entityById.has(entityId)) continue;
        const nextById = new Map(entry.entityById);
        nextById.delete(entityId);
        const nextList = entry.entities.filter((e) => e._id !== entityId && e.id !== entityId);
        nextCaches.set(spaceId, { ...entry, entities: nextList, entityById: nextById });
        mutated = true;
      }
      if (mutated) {
        useSpaceEntityStore.setState((s) => ({
          caches: nextCaches,
          bumpVersion: s.bumpVersion + 1,
        }));
      }
      return;
    }

    if (!data) return;

    const incomingSpaceId: string | null = data.spaceId ?? null;
    let mutated = false;
    const nextCaches = new Map(state.caches);

    for (const [spaceId, entry] of nextCaches) {
      const existing = entry.entityById.get(entityId);
      if (!existing && spaceId !== incomingSpaceId) continue;

      let next: BaseEntityType;
      if (existing) {
        next = { ...existing };
        if (data.name) next.name = data.name;
        if (data.icon !== undefined) (next as any).icon = data.icon;
        if (data.entityType) next.entityType = data.entityType;
        if (data.skeleton !== undefined) {
          (next as any).skeleton = { ...((existing as any).skeleton ?? {}), ...data.skeleton };
        }
        if (data.updated_at) (next as any).updated_at = data.updated_at;
      } else if (spaceId === incomingSpaceId && action === 'add') {
        next = {
          _id: entityId,
          id: entityId,
          name: data.name ?? 'Untitled',
          type: (data.entityType ?? 'unknown').toLowerCase(),
          entityType: data.entityType ?? 'unknown',
          icon: data.icon,
          spaceId: incomingSpaceId,
          skeleton: data.skeleton ?? {},
          created_at: data.created_at ?? new Date().toISOString(),
          updated_at: data.updated_at ?? new Date().toISOString(),
        } as unknown as BaseEntityType;
      } else {
        continue;
      }

      const nextById = new Map(entry.entityById);
      nextById.set(entityId, next);
      const nextList = existing
        ? entry.entities.map((e) => (e._id === entityId || e.id === entityId ? next : e))
        : [...entry.entities, next];
      nextCaches.set(spaceId, { ...entry, entities: nextList, entityById: nextById });
      idbPatchEntity(spaceId, next).catch(() => {});
      mutated = true;
    }

    if (mutated) {
      useSpaceEntityStore.setState((s) => ({
        caches: nextCaches,
        bumpVersion: s.bumpVersion + 1,
      }));
    }
  });
}

export async function dropSpaceEntityCache(spaceId: string): Promise<void> {
  useSpaceEntityStore.getState().evictSpace(spaceId);
  await deleteSnapshot(spaceId);
}
